import React, { useId } from 'react';
import { Box, useTheme } from '@mui/material';

type TourSpotlightProps = {
  targetRect: DOMRect | null;
  padding?: number;
  onBackdropClick?: () => void;
  zIndex: number;
  /** When true, clicks pass through the spotlight hole to the highlighted target. */
  allowTargetInteraction?: boolean;
};

const BACKDROP_COLOR = 'rgba(0,0,0,0.65)';

export const TourSpotlight: React.FC<TourSpotlightProps> = ({
  targetRect,
  padding = 8,
  onBackdropClick,
  zIndex,
  allowTargetInteraction = false,
}) => {
  const theme = useTheme();
  const maskId = useId().replace(/:/g, '');
  const spotlightMaskId = `tour-spotlight-mask-${maskId}`;
  // The dim sits under MUI's modal layer so a Select/Menu/Dialog the user opens
  // during a tour stays clickable — above the modal layer the overlay swallows
  // every outside click and dropdowns can never be dismissed. The highlight ring
  // keeps the tour z-index so the target stays marked over those surfaces.
  // Wrappers use `display: contents` on purpose: a positioned wrapper would form
  // a stacking context and collapse both layers back onto one z-index.
  const dimZIndex = Math.min(zIndex, theme.zIndex.modal - 1);

  if (!targetRect) {
    return (
      <Box
        aria-hidden
        className="apt-tour-overlay"
        onClick={onBackdropClick}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: dimZIndex,
          bgcolor: BACKDROP_COLOR,
        }}
      />
    );
  }

  const top = Math.max(0, targetRect.top - padding);
  const left = Math.max(0, targetRect.left - padding);
  const width = targetRect.width + padding * 2;
  const height = targetRect.height + padding * 2;
  const bottom = top + height;
  const right = left + width;

  const backdropPanelSx = {
    position: 'fixed' as const,
    zIndex: dimZIndex,
    bgcolor: BACKDROP_COLOR,
    pointerEvents: 'auto' as const,
  };

  const highlightRing = (
    <Box
      sx={{
        position: 'fixed',
        top,
        left,
        width,
        height,
        borderRadius: 1,
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        pointerEvents: 'none',
        zIndex,
      }}
    />
  );

  if (allowTargetInteraction) {
    return (
      <Box
        className="apt-tour-overlay"
        aria-hidden
        sx={{ display: 'contents', pointerEvents: 'none' }}
      >
        <Box
          sx={{ ...backdropPanelSx, top: 0, left: 0, right: 0, height: top }}
          onClick={onBackdropClick}
        />
        <Box
          sx={{ ...backdropPanelSx, top, left: 0, width: left, height }}
          onClick={onBackdropClick}
        />
        <Box
          sx={{ ...backdropPanelSx, top, left: right, right: 0, height }}
          onClick={onBackdropClick}
        />
        <Box
          sx={{ ...backdropPanelSx, top: bottom, left: 0, right: 0, bottom: 0 }}
          onClick={onBackdropClick}
        />
        {highlightRing}
      </Box>
    );
  }

  return (
    <Box
      className="apt-tour-overlay"
      aria-hidden
      sx={{ display: 'contents', pointerEvents: 'none' }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'fixed', inset: 0, zIndex: dimZIndex }}
      >
        <defs>
          <mask id={spotlightMaskId}>
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={left}
              y={top}
              width={width}
              height={height}
              rx={theme.shape.borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={BACKDROP_COLOR}
          mask={`url(#${spotlightMaskId})`}
          style={{ pointerEvents: 'auto' }}
          onClick={onBackdropClick}
        />
      </svg>
      {highlightRing}
    </Box>
  );
};

export default TourSpotlight;
